import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthProvider";
import { useCurrency } from "@/lib/CurrencyProvider";
import { toast } from "sonner";
import { Check, ArrowRight, LogIn } from "lucide-react";

const STEPS = ["Service", "Vet", "Pet", "Date & Time", "Review"];

export default function Book() {
  const { user, login } = useAuth();
  const { format } = useCurrency();
  const location = useLocation();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [services, setServices] = useState([]);
  const [team, setTeam] = useState([]);
  const [pets, setPets] = useState([]);
  const [slots, setSlots] = useState([]);
  const [choice, setChoice] = useState({
    service_id: location.state?.preselectService || "",
    vet_id: "",
    pet_id: "",
    date: new Date().toISOString().slice(0, 10),
    start_at: "",
    reason: "",
    mode: "in-person",
  });
  const [newPet, setNewPet] = useState({ name: "", species: "Dog", breed: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
    api.get("/team").then((r) => setTeam(r.data.filter((m) => m.role === "vet")));
  }, []);

  useEffect(() => {
    if (user) api.get("/pets").then((r) => setPets(r.data));
  }, [user]);

  useEffect(() => {
    if (choice.service_id && choice.date) {
      api.get("/appointments/slots", { params: { date: choice.date, service_id: choice.service_id, vet_id: choice.vet_id || undefined } })
        .then((r) => setSlots(r.data.slots));
    }
  }, [choice.service_id, choice.date, choice.vet_id]);

  const svc = services.find((s) => s.service_id === choice.service_id);
  const vet = team.find((t) => t.member_id === choice.vet_id);
  const pet = pets.find((p) => p.pet_id === choice.pet_id);

  const canNext = () => {
    if (step === 0) return !!choice.service_id;
    if (step === 1) return true;
    if (step === 2) return !!choice.pet_id;
    if (step === 3) return !!choice.start_at;
    return true;
  };

  const addPet = async () => {
    const { data } = await api.post("/pets", newPet);
    setPets([...pets, data]);
    setChoice({ ...choice, pet_id: data.pet_id });
    setNewPet({ name: "", species: "Dog", breed: "" });
    toast.success(`${data.name} added to your family`);
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/appointments", {
        service_id: choice.service_id,
        vet_id: choice.vet_id || null,
        pet_id: choice.pet_id,
        start_at: choice.start_at,
        reason: choice.reason,
        mode: choice.mode,
      });
      toast.success("Appointment confirmed!");
      navigate("/portal");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Booking failed");
    } finally { setBusy(false); }
  };

  if (!user) {
    return (
      <div className="min-h-screen ankara" data-testid="book-page-guest">
        <Header />
        <div className="max-w-md mx-auto py-24 px-4 text-center">
          <h1 className="font-display text-3xl">Sign in to book</h1>
          <p className="mt-3 text-[#57534E]">A Jay's account lets us keep your pets' history in one place.</p>
          <button onClick={login} className="mt-8 btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold" data-testid="book-signin-btn">
            <LogIn size={16} /> Sign in with Google
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF7E6]" data-testid="book-page">
      <Header />
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-24">
        <div className="text-xs uppercase tracking-[0.25em] text-[#8A9A5B]">Book appointment</div>
        <h1 className="font-display text-3xl md:text-4xl mt-2">Reserve a time for your pet.</h1>

        {/* Stepper */}
        <div className="mt-8 flex items-center gap-2 overflow-x-auto pb-2" data-testid="booking-stepper">
          {STEPS.map((s, i) => (
            <div key={s} className={`flex items-center gap-2 text-xs ${i <= step ? "text-[#0F766E]" : "text-[#9ca3af]"}`}>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-[11px] font-semibold ${i <= step ? "bg-[#0F766E]" : "bg-[#d1d5db]"}`}>
                {i < step ? <Check size={12} /> : i + 1}
              </span>
              <span className="whitespace-nowrap">{s}</span>
              {i < STEPS.length - 1 && <span className="w-6 h-px bg-[#e6e2d8]" />}
            </div>
          ))}
        </div>

        <div className="mt-8 bg-white border border-[#EAD9B0] rounded-3xl p-6 md:p-10">
          {step === 0 && (
            <div data-testid="step-service">
              <h2 className="font-display text-xl mb-4">Choose a service</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {services.map((s) => (
                  <button key={s.service_id} onClick={() => setChoice({ ...choice, service_id: s.service_id })} data-testid={`pick-service-${s.service_id}`}
                    className={`text-left p-4 rounded-xl border transition-colors ${choice.service_id === s.service_id ? "border-[#0F766E] bg-[#0F766E]/5" : "border-[#EAD9B0] hover:border-[#0F766E]"}`}>
                    <div className="text-[10px] uppercase tracking-widest text-[#8A9A5B]">{s.category}</div>
                    <div className="font-display text-lg mt-1">{s.name}</div>
                    <div className="mt-1 text-xs text-[#4b5563]">{s.duration_min} min · {s.price_display || `₦${s.price.toLocaleString()}`}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="step-vet">
              <h2 className="font-display text-xl mb-4">Choose a veterinarian <span className="text-sm font-normal text-[#4b5563]">(optional)</span></h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button onClick={() => setChoice({ ...choice, vet_id: "" })} data-testid="pick-vet-any"
                  className={`text-left p-4 rounded-xl border ${!choice.vet_id ? "border-[#0F766E] bg-[#0F766E]/5" : "border-[#EAD9B0] hover:border-[#0F766E]"}`}>
                  <div className="font-display text-lg">First available</div>
                  <div className="text-xs text-[#4b5563] mt-1">We'll match you to the earliest opening.</div>
                </button>
                {team.map((t) => (
                  <button key={t.member_id} onClick={() => setChoice({ ...choice, vet_id: t.member_id })} data-testid={`pick-vet-${t.member_id}`}
                    className={`text-left p-4 rounded-xl border flex items-center gap-3 ${choice.vet_id === t.member_id ? "border-[#0F766E] bg-[#0F766E]/5" : "border-[#EAD9B0] hover:border-[#0F766E]"}`}>
                    <img src={t.picture} alt={t.name} className="w-12 h-12 rounded-full object-cover" />
                    <div>
                      <div className="font-display text-base">{t.name}</div>
                      <div className="text-[11px] uppercase tracking-widest text-[#8A9A5B]">{t.title}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-pet">
              <h2 className="font-display text-xl mb-4">Which pet is this for?</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {pets.map((p) => (
                  <button key={p.pet_id} onClick={() => setChoice({ ...choice, pet_id: p.pet_id })} data-testid={`pick-pet-${p.pet_id}`}
                    className={`text-left p-4 rounded-xl border ${choice.pet_id === p.pet_id ? "border-[#0F766E] bg-[#0F766E]/5" : "border-[#EAD9B0] hover:border-[#0F766E]"}`}>
                    <div className="font-display text-lg">{p.name}</div>
                    <div className="text-xs text-[#4b5563]">{p.species}{p.breed ? ` · ${p.breed}` : ""}</div>
                  </button>
                ))}
              </div>
              <div className="mt-6 p-5 border border-dashed border-[#8A9A5B]/60 rounded-2xl">
                <div className="font-display text-base mb-3">Add a new pet</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <input placeholder="Name" value={newPet.name} onChange={(e) => setNewPet({ ...newPet, name: e.target.value })} data-testid="new-pet-name"
                    className="rounded-lg border border-[#EAD9B0] bg-[#FFF7E6] p-3 text-sm" />
                  <select value={newPet.species} onChange={(e) => setNewPet({ ...newPet, species: e.target.value })} data-testid="new-pet-species"
                    className="rounded-lg border border-[#EAD9B0] bg-[#FFF7E6] p-3 text-sm">
                    {["Dog", "Cat", "Rabbit", "Bird", "Reptile", "Other"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                  <input placeholder="Breed (optional)" value={newPet.breed} onChange={(e) => setNewPet({ ...newPet, breed: e.target.value })} data-testid="new-pet-breed"
                    className="rounded-lg border border-[#EAD9B0] bg-[#FFF7E6] p-3 text-sm" />
                </div>
                <button onClick={addPet} disabled={!newPet.name} className="mt-4 btn-gold px-5 py-2 rounded-full text-xs font-semibold disabled:opacity-50" data-testid="add-new-pet-btn">
                  Add pet
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-datetime">
              <h2 className="font-display text-xl mb-4">Pick a date &amp; time</h2>
              <input type="date" value={choice.date} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setChoice({ ...choice, date: e.target.value, start_at: "" })} data-testid="date-picker"
                className="rounded-lg border border-[#EAD9B0] bg-[#FFF7E6] p-3 text-sm" />
              <div className="mt-6 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="slots-grid">
                {slots.map((s) => {
                  const t = new Date(s.time);
                  const label = t.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });
                  const selected = choice.start_at === s.time;
                  return (
                    <button key={s.time} disabled={!s.available} onClick={() => setChoice({ ...choice, start_at: s.time })} data-testid={`slot-${label}`}
                      className={`p-2 rounded-lg border text-xs font-medium transition-colors ${
                        selected ? "bg-[#0F766E] text-white border-[#0F766E]" :
                        s.available ? "bg-white border-[#EAD9B0] hover:border-[#0F766E]" :
                        "bg-[#f3f4f6] text-[#9ca3af] border-[#f3f4f6] cursor-not-allowed line-through"
                      }`}>
                      {label}
                    </button>
                  );
                })}
              </div>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#4b5563]">Mode</label>
                  <select value={choice.mode} onChange={(e) => setChoice({ ...choice, mode: e.target.value })} data-testid="mode-select"
                    className="mt-2 w-full rounded-lg border border-[#EAD9B0] bg-[#FFF7E6] p-3 text-sm">
                    <option value="in-person">In-person visit</option>
                    <option value="virtual">Virtual consultation</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#4b5563]">Reason (optional)</label>
                  <input value={choice.reason} onChange={(e) => setChoice({ ...choice, reason: e.target.value })} data-testid="reason-input"
                    className="mt-2 w-full rounded-lg border border-[#EAD9B0] bg-[#FFF7E6] p-3 text-sm" />
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div data-testid="step-review">
              <h2 className="font-display text-xl mb-4">Review &amp; confirm</h2>
              <div className="rounded-2xl bg-[#FFF7E6] border border-[#EAD9B0] p-5 text-sm space-y-2">
                <Row k="Service" v={svc?.name} />
                <Row k="Veterinarian" v={vet?.name || "First available"} />
                <Row k="Pet" v={pet?.name} />
                <Row k="When" v={choice.start_at ? new Date(choice.start_at).toLocaleString([], { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""} />
                <Row k="Duration" v={`${svc?.duration_min} min`} />
                <Row k="Mode" v={choice.mode === "virtual" ? "Virtual" : "In-person"} />
                <Row k="Estimated fee" v={svc ? (svc.price_display || format(svc.price)) : ""} />
                {choice.reason && <Row k="Reason" v={choice.reason} />}
              </div>
              <p className="mt-4 text-xs text-[#4b5563]">Payment collected at the clinic. You will receive an email confirmation.</p>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} data-testid="step-back"
              className="text-sm text-[#4b5563] disabled:opacity-40">Back</button>
            {step < STEPS.length - 1 ? (
              <button onClick={() => setStep(step + 1)} disabled={!canNext()} data-testid="step-next"
                className="btn-primary px-6 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
                Continue <ArrowRight size={15} />
              </button>
            ) : (
              <button onClick={confirm} disabled={busy} data-testid="confirm-booking"
                className="btn-gold px-6 py-3 rounded-full text-sm font-semibold disabled:opacity-50">
                {busy ? "Booking…" : "Confirm booking"}
              </button>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between"><span className="text-[#4b5563]">{k}</span><span className="text-[#1F2937] font-medium">{v || "—"}</span></div>;
}
